import { useFormik } from "formik";
import { adminUpdateWorkLocation } from "helpers/backend_helpers";
import PropTypes from "prop-types";
import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import {
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  InputGroup,
  Button,
} from "reactstrap";
import * as Yup from "yup";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const customIcon = L.divIcon({
  className: "custom-marker",
  html: `
    <div style="position: relative;">
      <div style="
        width: 32px;
        height: 32px;
        background: #EA4335;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      "></div>
      <div style="
        position: absolute;
        top: 8px;
        left: 8px;
        width: 12px;
        height: 12px;
        background: white;
        border-radius: 50%;
        transform: rotate(45deg);
      "></div>
    </div>
  `,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const UpdateModalWorkLocation = ({
  show,
  onCloseClick,
  getData,
  data,
}: any) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationPermission, setLocationPermission] =
    useState<string>("prompt");
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);

  const checkLocationPermission = async () => {
    if (navigator.permissions) {
      try {
        const result = await navigator.permissions.query({
          name: "geolocation",
        });
        setLocationPermission(result.state);

        result.addEventListener("change", () => {
          setLocationPermission(result.state);
        });
      } catch (error) {
        console.log("Permission API not supported");
      }
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation tidak didukung oleh browser Anda!");
      return;
    }

    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    if (!isMobile) {
      toast.info(
        <div>
          <strong>Mengambil lokasi di laptop...</strong>
          <br />
          <small>
            Ini mungkin memakan waktu lebih lama. Pastikan WiFi aktif.
          </small>
        </div>,
        { autoClose: 3000 }
      );
    }

    setIsGettingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        updateMarker(latitude, longitude);
        reverseGeocode(latitude, longitude);
        if (mapRef.current) {
          mapRef.current.setView([latitude, longitude], 17);
        }
        toast.success("Lokasi Anda berhasil ditemukan!");
        setIsGettingLocation(false);
      },
      (error) => {
        setIsGettingLocation(false);

        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error(
              <div>
                <strong>Permission Ditolak!</strong>
                <br />
                <small>
                  {isMobile
                    ? "Silakan aktifkan izin lokasi di pengaturan browser Anda."
                    : "Aktifkan Location Service di Windows Settings → Privacy → Location"}
                </small>
              </div>,
              { autoClose: 5000 }
            );
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error(
              <div>
                <strong>Informasi lokasi tidak tersedia!</strong>
                <br />
                <small>
                  {isMobile
                    ? "Pastikan GPS aktif."
                    : "Pastikan WiFi aktif dan Location Service Windows diaktifkan."}
                </small>
              </div>,
              { autoClose: 5000 }
            );
            break;
          case error.TIMEOUT:
            toast.error(
              <div>
                <strong>Request lokasi timeout!</strong>
                <br />
                <small>
                  {isMobile
                    ? "Coba lagi atau pastikan GPS aktif."
                    : "Laptop memerlukan WiFi untuk lokasi. Coba gunakan fitur pencarian alamat sebagai alternatif."}
                </small>
              </div>,
              { autoClose: 6000 }
            );
            break;
          default:
            toast.error("Terjadi kesalahan saat mengambil lokasi!");
            break;
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 20000,
        maximumAge: 30000,
      }
    );
  };

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: data?.name || "",
      address: data?.address || "",
      lat: data?.lat || "",
      lon: data?.lon || "",
      radius: data?.radius ? (parseFloat(data.radius) * 1000).toString() : "", // Konversi km ke meter untuk UI
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Nama lokasi harus diisi!"),
      address: Yup.string(),
      lat: Yup.string().required("Latitude harus diisi!"),
      lon: Yup.string().required("Longitude harus diisi!"),
      radius: Yup.string().required("Radius harus diisi!"),
    }),
    onSubmit: (values) => {
      // Konversi radius dari meter ke kilometer sebelum dikirim
      const dataToSend = {
        ...values,
        radius: (parseFloat(values.radius) / 1000).toString(), // Meter ke kilometer
      };

      adminUpdateWorkLocation(data._id, dataToSend)
        .then((response: any) => {
          onCloseClick();
          getData();
          toast.success(
            <div>
              Lokasi kerja <b>{response?.name}</b> berhasil diubah!
            </div>
          );
          validation.resetForm();
        })
        .catch(() => {});
    },
  });

  useEffect(() => {
    if (show && !mapRef.current) {
      setTimeout(() => {
        const mapElement = document.getElementById("map");
        if (mapElement && !mapRef.current) {
          const map = L.map("map").setView([-7.5707, 110.8275], 13);

          L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution: "© OpenStreetMap contributors",
            maxZoom: 19,
          }).addTo(map);

          map.on("click", (e: L.LeafletMouseEvent) => {
            updateMarker(e.latlng.lat, e.latlng.lng);
            reverseGeocode(e.latlng.lat, e.latlng.lng);
          });

          mapRef.current = map;

          if (data?.lat && data?.lon) {
            const lat = parseFloat(data.lat);
            const lon = parseFloat(data.lon);
            if (!isNaN(lat) && !isNaN(lon) && lat !== 0 && lon !== 0) {
              setTimeout(() => {
                updateMarker(lat, lon, false);
                map.setView([lat, lon], 15);
              }, 100);
            }
          }
        }
      }, 300);
    }

    return () => {
      if (!show && mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
        circleRef.current = null;
      }
    };
  }, [show, data]);

  useEffect(() => {
    if (mapRef.current && data?.lat && data?.lon && !markerRef.current) {
      const lat = parseFloat(data.lat);
      const lon = parseFloat(data.lon);

      if (!isNaN(lat) && !isNaN(lon) && lat !== 0 && lon !== 0) {
        updateMarker(lat, lon, false);
        mapRef.current.setView([lat, lon], 15);
      }
    }
  }, [data, mapRef.current]);

  useEffect(() => {
    if (mapRef.current && markerRef.current) {
      const lat = parseFloat(validation.values.lat);
      const lon = parseFloat(validation.values.lon);

      if (!isNaN(lat) && !isNaN(lon)) {
        if (circleRef.current) {
          mapRef.current.removeLayer(circleRef.current);
        }

        if (validation.values.radius) {
          const radiusInMeters = parseFloat(validation.values.radius); // Sudah dalam meter
          const circle = L.circle([lat, lon], {
            radius: radiusInMeters,
            color: "#4285F4",
            fillColor: "#4285F4",
            fillOpacity: 0.1,
            weight: 2,
          }).addTo(mapRef.current);
          circleRef.current = circle;
        }
      }
    }
  }, [validation.values.radius]);

  useEffect(() => {
    if (
      markerRef.current &&
      (validation.values.name || validation.values.address)
    ) {
      const lat = parseFloat(validation.values.lat);
      const lon = parseFloat(validation.values.lon);

      if (!isNaN(lat) && !isNaN(lon)) {
        const locationName =
          validation.values.name || data?.name || "Lokasi Kerja";
        const locationAddress =
          validation.values.address || data?.address || "Alamat belum diisi";

        const popupContent = `
        <div style="min-width: 200px;">
          <h6 style="margin: 0 0 8px 0; font-weight: 600;">${locationName}</h6>
          <p style="margin: 0 0 4px 0; font-size: 13px; color: #666;">
            ${locationAddress}
          </p>
          <p style="margin: 0; font-size: 12px; color: #999;">
            <strong>Koordinat:</strong><br/>
            ${lat.toFixed(6)}, ${lon.toFixed(6)}
          </p>
        </div>
      `;

        markerRef.current.setPopupContent(popupContent);
      }
    }
  }, [
    validation.values.name,
    validation.values.address,
    validation.values.lat,
    validation.values.lon,
  ]);

  const updateMarker = (
    lat: number,
    lon: number,
    updateFormValues: boolean = true
  ) => {
    if (!mapRef.current) return;

    if (markerRef.current) {
      mapRef.current.removeLayer(markerRef.current);
    }
    if (circleRef.current) {
      mapRef.current.removeLayer(circleRef.current);
    }

    const marker = L.marker([lat, lon], { icon: customIcon }).addTo(
      mapRef.current
    );

    const locationName = validation.values.name || data?.name || "Lokasi Kerja";
    const locationAddress =
      validation.values.address || data?.address || "Alamat belum diisi";

    const popupContent = `
    <div style="min-width: 200px;">
      <h6 style="margin: 0 0 8px 0; font-weight: 600;">${locationName}</h6>
      <p style="margin: 0 0 4px 0; font-size: 13px; color: #666;">
        ${locationAddress}
      </p>
      <p style="margin: 0; font-size: 12px; color: #999;">
        <strong>Koordinat:</strong><br/>
        ${lat.toFixed(6)}, ${lon.toFixed(6)}
      </p>
    </div>
  `;

    marker.bindPopup(popupContent);
    markerRef.current = marker;

    if (validation.values.radius) {
      const radiusInMeters = parseFloat(validation.values.radius); // Sudah dalam meter
      const circle = L.circle([lat, lon], {
        radius: radiusInMeters,
        color: "#4285F4",
        fillColor: "#4285F4",
        fillOpacity: 0.1,
        weight: 2,
      }).addTo(mapRef.current);
      circleRef.current = circle;
    }

    if (updateFormValues) {
      validation.setFieldValue("lat", lat.toString());
      validation.setFieldValue("lon", lon.toString());
    }
  };

  const reverseGeocode = async (lat: number, lon: number) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`
      );
      const data = await response.json();
      if (data.display_name) {
        validation.setFieldValue("address", data.display_name);
      }
    } catch (error) {
      console.error("Reverse geocoding failed:", error);
    }
  };

  const searchLocation = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          searchQuery
        )}&limit=1`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const { lat, lon, display_name } = data[0];
        updateMarker(parseFloat(lat), parseFloat(lon));
        validation.setFieldValue("address", display_name);
        if (mapRef.current) {
          mapRef.current.setView([parseFloat(lat), parseFloat(lon)], 15);
        }
        toast.success("Lokasi ditemukan!");
      } else {
        toast.error("Lokasi tidak ditemukan!");
      }
    } catch (error) {
      toast.error("Gagal mencari lokasi!");
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (
      markerRef.current &&
      (validation.values.name || validation.values.address)
    ) {
      const lat = parseFloat(validation.values.lat);
      const lon = parseFloat(validation.values.lon);

      if (!isNaN(lat) && !isNaN(lon)) {
        const locationName = validation.values.name || "Lokasi Kerja";
        const locationAddress =
          validation.values.address || "Alamat belum diisi";

        const popupContent = `
        <div style="min-width: 200px;">
          <h6 style="margin: 0 0 8px 0; font-weight: 600;">${locationName}</h6>
          <p style="margin: 0 0 4px 0; font-size: 13px; color: #666;">
            ${locationAddress}
          </p>
          <p style="margin: 0; font-size: 12px; color: #999;">
            <strong>Koordinat:</strong><br/>
            ${lat.toFixed(6)}, ${lon.toFixed(6)}
          </p>
        </div>
      `;

        markerRef.current.setPopupContent(popupContent);
      }
    }
  }, [
    validation.values.name,
    validation.values.address,
    validation.values.lat,
    validation.values.lon,
  ]);

  useEffect(() => {
    if (!show) {
      validation.resetForm();
      setSearchQuery("");
    } else {
      checkLocationPermission();
    }
  }, [show]);

  return (
    <Modal
      size="xl"
      fade={true}
      isOpen={show}
      toggle={onCloseClick}
      centered={true}
    >
      <ModalHeader className="bg-light p-3" toggle={onCloseClick}>
        <i className="ri-map-pin-line me-2"></i>
        Edit Lokasi Kerja
      </ModalHeader>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <ModalBody>
          <div className="row">
            <div className="col-lg-5">
              <div className="mb-3">
                <Label htmlFor="name" className="form-label">
                  Nama Lokasi Kerja <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  className="form-control"
                  id="name"
                  name="name"
                  placeholder="Contoh: Kantor Pusat"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.name || ""}
                  invalid={
                    validation.touched.name && validation.errors.name
                      ? true
                      : false
                  }
                />
                {validation.errors.name && validation.touched.name ? (
                  <FormFeedback type="invalid">
                    {validation.errors.name}
                  </FormFeedback>
                ) : null}
              </div>

              <div className="mb-3">
                <Label htmlFor="address" className="form-label">
                  Alamat
                </Label>
                <Input
                  disabled
                  type="textarea"
                  className="form-control"
                  id="address"
                  rows={3}
                  placeholder="Alamat lengkap lokasi kerja"
                  name="address"
                  value={validation.values.address || ""}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                />
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <Label htmlFor="lat" className="form-label">
                    Latitude <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    className="form-control"
                    id="lat"
                    name="lat"
                    placeholder="-7.5707"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.lat || ""}
                    invalid={
                      validation.touched.lat && validation.errors.lat
                        ? true
                        : false
                    }
                  />
                  {validation.errors.lat && validation.touched.lat ? (
                    <FormFeedback type="invalid">
                      {validation.errors.lat}
                    </FormFeedback>
                  ) : null}
                </div>

                <div className="col-md-6 mb-3">
                  <Label htmlFor="lon" className="form-label">
                    Longitude <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    className="form-control"
                    id="lon"
                    name="lon"
                    placeholder="110.8275"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.lon || ""}
                    invalid={
                      validation.touched.lon && validation.errors.lon
                        ? true
                        : false
                    }
                  />
                  {validation.errors.lon && validation.touched.lon ? (
                    <FormFeedback type="invalid">
                      {validation.errors.lon}
                    </FormFeedback>
                  ) : null}
                </div>
              </div>

              <div className="mb-3">
                <Label htmlFor="radius" className="form-label">
                  Radius Toleransi (meter){" "}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  type="number"
                  step="1"
                  className="form-control"
                  id="radius"
                  name="radius"
                  placeholder="Contoh: 100"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.radius || ""}
                  invalid={
                    validation.touched.radius && validation.errors.radius
                      ? true
                      : false
                  }
                />
                {validation.errors.radius && validation.touched.radius ? (
                  <FormFeedback type="invalid">
                    {validation.errors.radius}
                  </FormFeedback>
                ) : null}
                <small className="text-muted">
                  Jarak maksimal untuk absensi dari titik lokasi
                </small>
              </div>
            </div>

            <div className="col-lg-7">
              <div className="mb-3">
                <Label className="form-label">
                  <i className="ri-search-line me-1"></i>
                  Cari Lokasi
                </Label>
                <div className="d-flex gap-2">
                  <InputGroup style={{ flex: 1 }}>
                    <Input
                      type="text"
                      placeholder="Cari alamat atau nama tempat..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          searchLocation();
                        }
                      }}
                    />
                    <Button
                      color="primary"
                      onClick={searchLocation}
                      disabled={isSearching}
                    >
                      {isSearching ? (
                        <span className="spinner-border spinner-border-sm me-1"></span>
                      ) : (
                        <i className="ri-search-line"></i>
                      )}
                    </Button>
                  </InputGroup>

                  <Button
                    color="info"
                    onClick={getCurrentLocation}
                    disabled={isGettingLocation}
                    title={
                      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                        navigator.userAgent
                      )
                        ? "Gunakan lokasi saya saat ini"
                        : "Gunakan lokasi saya (WiFi-based di laptop, mungkin kurang akurat)"
                    }
                  >
                    {isGettingLocation ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      <i className="ri-navigation-line"></i>
                    )}
                  </Button>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-2">
                  <small className="text-muted">
                    <i className="ri-information-line me-1"></i>
                    Klik pada peta untuk menentukan lokasi
                  </small>

                  {locationPermission === "denied" && (
                    <small className="text-danger">
                      <i className="ri-alert-line me-1"></i>
                      Izin lokasi ditolak
                    </small>
                  )}

                  {locationPermission === "granted" && (
                    <small className="text-success">
                      <i className="ri-checkbox-circle-line me-1"></i>
                      Izin lokasi aktif
                    </small>
                  )}
                </div>
              </div>

              <div
                id="map"
                style={{
                  height: "400px",
                  borderRadius: "8px",
                  border: "2px solid #e9ecef",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              ></div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-soft-danger waves-effect waves-light material-shadow-none btn-sm"
            onClick={onCloseClick}
          >
            <i className="ri-close-line me-1"></i>
            Batal
          </button>
          <button
            type="submit"
            className="btn btn-soft-success waves-effect waves-light material-shadow-none btn-sm"
          >
            <i className="ri-save-line me-1"></i>
            Simpan
          </button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

UpdateModalWorkLocation.propTypes = {
  onCloseClick: PropTypes.func,
  show: PropTypes.any,
  getData: PropTypes.func,
  data: PropTypes.any,
};

export default UpdateModalWorkLocation;
